// Shared forecast math for the cash-flow forecasts (expense + P&L).

/** Mean of a numeric series. */
export function mean(ys: number[]): number {
  if (!ys.length) return 0;
  return ys.reduce((s, v) => s + v, 0) / ys.length;
}

/**
 * Project a series forward with a least-squares linear trend.
 *
 * Fits `y = intercept + slope·x` over the historical points at x = 0..n-1, then
 * evaluates it at the next `horizon` indices (n, n+1, …). Each projected month
 * therefore follows the recent trajectory rather than repeating a flat average.
 * Values are clamped to ≥ 0 unless `allowNegative` (revenue/costs can't go
 * negative; a contribution-profit series can).
 */
export function projectTrend(
  ys: number[],
  horizon: number,
  allowNegative = false
): number[] {
  const n = ys.length;
  if (n === 0) return Array.from({ length: horizon }, () => 0);
  const avg = mean(ys);
  const xbar = (n - 1) / 2;
  let num = 0;
  let den = 0;
  for (let i = 0; i < n; i++) {
    num += (i - xbar) * (ys[i] - avg);
    den += (i - xbar) ** 2;
  }
  const slope = den ? num / den : 0;
  const intercept = avg - slope * xbar;
  return Array.from({ length: horizon }, (_, j) => {
    const v = intercept + slope * (n + j);
    return allowNegative ? v : Math.max(0, v);
  });
}
